import { useEffect, useState } from 'react';
import { Alert, Button, ProgressBar } from 'react-bootstrap';
import { CheckCircleFill, SlashCircle, XCircleFill } from 'react-bootstrap-icons';
import { formatSeconds } from '@/utils/relativeTime';
import { reasonOf } from './clientSelectionUtils';
import type { ToolProgress, ToolExecutionStatus } from '@/types/tools';
import './ProgressTracker.css';

interface ProgressTrackerProps {
  status: ToolExecutionStatus;
  progress?: ToolProgress;
  error?: string;
  startedAt?: Date;
  completedAt?: Date;
  /** Shows a Stop button while the run is going. */
  onStop?: () => void;
  /** What one step of the count is: "client" (the default) or "account". */
  unit?: string;
}

const STATUS_TEXT: Record<ToolExecutionStatus, string> = {
  idle: 'Not started',
  running: 'Running',
  completed: 'Finished',
  failed: 'Did not finish',
  cancelled: 'Stopped',
};

const count = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * The step a run is on, in plain words. The report services send short
 * engineering phrases ("Processing acme (3/40)..."); a person reading the page
 * gets "Reading acme: client 3 of 40". A phrase this does not know is shown as
 * sent, without its trailing dots.
 */
function plainProgress(message: string, unit: string): string {
  const text = message.trim();

  const one = text.match(/^Processing (.+) \((\d+)\/(\d+)\)/);
  if (one) return `Reading ${one[1]}: ${unit} ${one[2]} of ${one[3]}`;

  const accounts = text.match(/^Processing (\d+) account\(s\) \((\d+) client\(s\)\)/);
  if (accounts) {
    return `Reading ${count(Number(accounts[1]), 'account')} for ${count(Number(accounts[2]), 'client')}`;
  }

  const many = text.match(/^Processing (\d+) client\(s\)/);
  if (many) return `Reading ${count(Number(many[1]), 'client')}`;

  const done = text.match(/^Processed (\d+)\/(\d+)/);
  if (done) return `Read ${done[1]} of ${done[2]} accounts and regions`;

  if (/^(Initializing|Starting)/i.test(text)) return 'Starting';
  if (/^Loading client configuration/i.test(text)) return 'Loading the client list';
  if (/^Aggregating/i.test(text)) return 'Putting the results together';
  if (/^(Generating|Preparing).*files/i.test(text)) return 'Building the files to download';
  if (/^Discovering .*quota definitions/i.test(text)) return 'Finding the limits Bedrock has for these models';
  if (/^Fetching quotas across/i.test(text)) return 'Reading the limits in each account and region';
  if (/^Fetching quota values/i.test(text)) return 'Reading the limits';
  if (/^Fetching cost forecasts/i.test(text)) return "Reading this month's forecast";
  if (/complete(d)?!?$/i.test(text)) return 'Finished';

  return text.replace(/(\.{3}|…|!)$/, '');
}

/** Where a long run is up to: the step in words, how far through, and how long so far. */
export function ProgressTracker({
  status,
  progress,
  error,
  startedAt,
  completedAt,
  onStop,
  unit = 'client',
}: ProgressTrackerProps) {
  // The clock keeps moving between progress reports, so a slow step does not look stuck.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (status !== 'running') return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [status]);

  const elapsed = startedAt
    ? formatSeconds(((completedAt ? completedAt.getTime() : now) - startedAt.getTime()) / 1000)
    : '';

  const percent =
    progress && progress.total > 0 ? Math.min(100, Math.max(0, (progress.current / progress.total) * 100)) : 0;

  return (
    <div className={`tool-progress tool-progress--${status}`}>
      <div className="tool-progress__head">
        <span className="tool-progress__status">
          {status === 'completed' && <CheckCircleFill aria-hidden="true" />}
          {status === 'failed' && <XCircleFill aria-hidden="true" />}
          {status === 'cancelled' && <SlashCircle aria-hidden="true" />}
          {status === 'running' && <span className="tool-progress__pulse" aria-hidden="true" />}
          {STATUS_TEXT[status]}
        </span>
        {elapsed && (
          <span className="tool-progress__time">{status === 'running' ? `${elapsed} so far` : `after ${elapsed}`}</span>
        )}
        {status === 'running' && onStop && (
          <Button size="sm" variant="outline-secondary" className="tool-progress__stop" onClick={onStop}>
            Stop
          </Button>
        )}
      </div>

      {status === 'running' && progress && (
        <>
          <ProgressBar now={percent} aria-label="Progress" />
          <div className="tool-progress__step">
            <span>{plainProgress(progress.message, unit)}</span>
            <span className="numeric">{Math.round(percent)}%</span>
          </div>
          {progress.details && <div className="tool-progress__details">{progress.details}</div>}
        </>
      )}

      {status === 'failed' && (
        <Alert variant="danger" className="tool-progress__alert">
          {reasonOf(error)} Change the choices and run it again.
        </Alert>
      )}

      {status === 'cancelled' && <div className="tool-progress__details">You stopped this run. Nothing was kept.</div>}
    </div>
  );
}
